/// <amd-module name="Repos/Views/Settings/Repositories-Policies/Components/StatusPolicies" />
import React = require("react");
import { IStatusPolicy } from "Repos/Views/Settings/Repositories-Policies/Contracts/Contracts";
import * as Resources from "Repos/Views/Settings/Repositories-Policies/Resources";
import { IObservableArray, IReadonlyObservableArray, ObservableArray, ObservableValue } from "VSS/Core/Observable";
import { localeIgnoreCaseComparer } from "VSS/Core/Util/String";
import { showMessageDialog } from "VSS/Features/PlatformUI/MessageDialog";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { Filter, getKeywordFilterItem } from "VSSUI/Filter";
import { IHeaderCommandBarItem } from "VSSUI/HeaderCommandBar";
import { IMenuItem, IMenuProps } from "VSSUI/Menu";
import { Observer } from "VSSUI/Observer";
import { ColumnMore, ColumnSorting, ITableColumn, ITableRow, SimpleTableCell, sortItems, SortOrder, Table, TableColumnLayout } from "VSSUI/Table";
import { Tooltip } from "VSSUI/TooltipEx";
import { Filter as FilterStore, FILTER_APPLIED_EVENT, IFilter } from "VSSUI/Utilities/Filter";
import { IPoliciesViewService, policyTypes } from "../Services/PoliciesViewService";
import { enabledColumn, inheritanceColumn, IPolicyColumn, pathColumn } from "./Columns";
import { PolicyCard } from "./PolicyCard";

interface IStatusPoliciesProps {
    isEditable: boolean;
    ready: boolean;
    policies: IReadonlyObservableArray<IStatusPolicy>;
}

const loadingItems = new ObservableArray<IStatusPolicy>(new Array(3).fill(new ObservableValue<IStatusPolicy | undefined>(undefined)));

export class StatusPolicies extends VssComponent<IStatusPoliciesProps> {
    private columns: IPolicyColumn<IStatusPolicy>[];
    private filter: IFilter;
    private filteredItems = new ObservableArray<IStatusPolicy>();
    private policyService: IPoliciesViewService;
    private sortColumnIndex: number = 1;
    private sortFunctions: ((a: IStatusPolicy, b: IStatusPolicy) => number)[];
    private sortOrder: SortOrder = SortOrder.ascending;

    private sortingBehavior = new ColumnSorting<IStatusPolicy>((columnIndex, order) => {
        this.sortColumnIndex = columnIndex;
        this.sortOrder = order;
        this.refreshItems();
    });

    public constructor(props: IStatusPoliciesProps, context: IVssComponentContext) {
        super(props, context);

        this.filter = new FilterStore();

        this.policyService = context.pageContext.getService<IPoliciesViewService>("PoliciesViewService");

        this.columns = this.getColumns();
        this.sortFunctions = this.columns.map(c => c.sortFunction!);
        this.refreshItems();

        this.observe([{ propertyName: "policies", observer: this.refreshItems }]);
    }

    public componentDidMount = () => {
        this.filter.subscribe(this.refreshItems, FILTER_APPLIED_EVENT);
    };

    public componentWillUnmount() {
        this.filter.unsubscribe(this.refreshItems);
    }

    public render = (): JSX.Element => {
        const { ready } = this.props;
        return (
            <Observer items={this.filteredItems}>
                {() => {
                    const itemCount = this.filteredItems.length;
                    return (
                        <PolicyCard
                            className="flex-grow policies"
                            filter={<Filter filterStore={this.filter} filterItems={[getKeywordFilterItem(this.filter, 400)]} items={[]} />}
                            headerCommandBarItems={this.getCommandBarItems()}
                            headerDescriptionText={Resources.StatusChecksDescription}
                            itemCount={itemCount}
                            policyName="status"
                            titleText={Resources.StatusChecks}
                        >
                            <div className="settings-policies-wrapper">
                                {!ready || itemCount ? (
                                    <Table<IStatusPolicy>
                                        behaviors={[this.sortingBehavior]}
                                        itemProvider={ready ? this.filteredItems : loadingItems}
                                        columns={this.columns}
                                        onActivate={(event, row) => {
                                            if (!event.defaultPrevented) {
                                                this.onEdit(row);
                                            }
                                        }}
                                    />
                                ) : (
                                    <div className="flex-grow secondary-text padding-horizontal-20">{Resources.ZeroStatus}</div>
                                )}
                            </div>
                        </PolicyCard>
                    );
                }}
            </Observer>
        );
    };

    private getColumns = (): IPolicyColumn<IStatusPolicy>[] => {
        return [
            enabledColumn,
            {
                columnLayout: TableColumnLayout.twoLine,
                id: "name",
                name: Resources.Name,
                readonly: true,
                renderCell: (
                    rowIndex: number,
                    columnIndex: number,
                    tableColumn: ITableColumn<IStatusPolicy>,
                    tableItem: IStatusPolicy
                ): JSX.Element => {
                    const cellValue = getRowTitle(tableItem);

                    return (
                        <SimpleTableCell columnIndex={columnIndex} key={columnIndex} tableColumn={tableColumn}>
                            <div className="flex-column text-ellipsis">
                                <div className="text-ellipsis">
                                    <Tooltip text={cellValue}>
                                        <span className="body-m font-weight-semibold">{cellValue}</span>
                                    </Tooltip>
                                </div>
                                <div className="text-ellipsis">
                                    <span className="secondary-text body-s">
                                        {tableItem.complexSettings!.required ? Resources.Required : Resources.Optional}
                                    </span>
                                </div>
                            </div>
                        </SimpleTableCell>
                    );
                },
                sortFunction: (a, b) => localeIgnoreCaseComparer(getRowTitle(a), getRowTitle(b)),
                sortProps: { ariaLabelAscending: Resources.SortByNameAsc, ariaLabelDescending: Resources.SortByNameDesc },
                width: -45
            },
            pathColumn,
            inheritanceColumn,
            new ColumnMore(this.getMoreMenuItems, this.showMoreMenu)
        ];
    };

    private getCommandBarItems = (): IHeaderCommandBarItem[] => {
        return [
            {
                ariaLabel: Resources.AddNewStatusPolicy,
                iconProps: { iconName: "Add" },
                id: "add-status-policy",
                disabled: !this.props.isEditable,
                onActivate: () => {
                    const newPolicy = this.policyService.getNewPolicy<IStatusPolicy>(policyTypes.statusChecks);
                    this.policyService.showEditPanel(newPolicy);
                }
            }
        ];
    };

    private getMoreMenuItems = (item: IStatusPolicy): IMenuProps => {
        const itemArr: IObservableArray<IMenuItem> = new ObservableArray([
            {
                id: "edit-view",
                text: Resources.EditView,
                data: item,
                onActivate: this.onEdit,
                iconProps: {
                    iconName: "View"
                }
            }
        ]);

        if (!item.isInherited && !item.isEnterpriseManaged) {
            itemArr.push({
                id: "delete",
                text: Resources.Delete,
                data: item,
                onActivate: this.onDelete,
                iconProps: {
                    iconName: "Delete"
                }
            });
        }

        const menuProps: IMenuProps = {
            id: "sub-menu",
            items: itemArr
        };

        return menuProps;
    };

    private onDelete = (menuItem: IMenuItem) => {
        const policy: IStatusPolicy = menuItem.data;
        showMessageDialog(this.context.pageContext, {
            lightDismiss: true,
            message: Resources.ConfirmDeletionMessage,
            okButtonProps: { danger: true, primary: false, text: Resources.Delete },
            title: Resources.ConfirmDeletion,
            onClose: result => {
                if (result) {
                    this.policyService.deleteStatusPolicy(policy);
                }
            }
        });
    };

    private onEdit = (item: IMenuItem | ITableRow<IStatusPolicy>) => {
        this.policyService.showEditPanel(item.data);
    };

    private refreshItems = () => {
        const filterItemState = this.filter.getState()["keyword"];
        const filter = filterItemState && filterItemState.value ? filterItemState.value.toLocaleLowerCase() : "";

        let items = this.props.policies ? [...this.props.policies.value] : [];
        if (filter) {
            items = items.filter(pol => {
                const { defaultDisplayName, filenamePatterns, status } = pol.complexSettings!;

                return (
                    (defaultDisplayName && defaultDisplayName.toLowerCase().indexOf(filter) !== -1) ||
                    (status && status.toLowerCase().indexOf(filter) !== -1) ||
                    (filenamePatterns && filenamePatterns.toLowerCase().indexOf(filter) !== -1)
                );
            });
        }

        this.filteredItems.value = sortItems(this.sortColumnIndex, this.sortOrder, this.sortFunctions, this.columns, items);
    };

    private showMoreMenu = (item: IStatusPolicy): boolean => {
        return !item.isInherited;
    };
}

function getRowTitle(policy: IStatusPolicy) {
    return policy.complexSettings!.defaultDisplayName || policy.complexSettings!.status;
}
